import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Card from '@src/components/Card';
import SearchInput from '@src/new-components/SearchInput';
import MonetizationOnRoundedIcon from '@mui/icons-material/MonetizationOnRounded';
import { Box } from '@mui/material';
import { useHistory, useLocation } from 'react-router-dom';
import ROUTES from '@src/constants/route';
import { Search, AddRounded } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import Typography from '@src/new-components/Typography';
import CommonButton from '@src/new-components/Button';
import Tabs from '@src/new-components/Tabs';
import { debounce } from 'lodash';
import {
  INSTANT_VOICE_CLONE,
  PRO_VOICE_CLONE,
  VOICE_CLONE_TYPE,
  VOICE_CLONING_TYPE,
  VOICE_COMPLETED,
  VOICE_NOT_USED,
  VOICE_PROCESSING,
} from '@src/constants/voiceCloning';
import actions from '@src/redux/actions';
import { nonAccentVietnamese } from '@src/utils/accent';
import { DEBOUNCE_DELAY } from '@src/constants/dictionary';
import { COLOR } from '@src/styles/color';
import CommonDialog from '@src/new-components/Dialog/CommonDialog';
import useUserInfo from '@src/hooks/useTtsUser';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import useLocationInfo from '@src/hooks/useLocation';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import ActionDialog from '@src/new-components/Dialog/ActionDialog';
import WarningIcon from '@src/assets/icons/warning-v2-with-padding.svg';
import { checkHasEverHadCommunityVoice } from '@src/apis/user';
import Voices from './Voices';
import InstantVoiceCloningOption from '../Home/InstantVoiceCloning';
import ProVoiceCloningOption from '../Home/ProVoiceCloning';
import {
  StyledHeaderVoiceList,
  StyledVoiceCloningList,
  StyledVoiceCloningOption,
} from './index.style';
import PromoBannerVoiceCloning from './PromoBannerVoiceCloning';

const VoiceCloningList = ({
  voices: voiceList,
  handleOpenPopupInstantVoiceCloning,
  handleOpenPopupProfessionalVoiceCloneUpload,
  setVoiceList,
  onContinueUploadVoice,
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();
  const location = useLocation();
  const { type: voiceCloningType } = location.state || {};
  const { ttsUser } = useUserInfo();
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState(INSTANT_VOICE_CLONE);
  const [voices, setVoices] = useState(voiceList);
  const [showPromoBanner, setShowPromoBanner] = useState(true);
  const [
    showPopupNotEligibleForMonetization,
    setShowPopupNotEligibleForMonetization,
  ] = useState(false);

  const { getFeatureValue } = useFeatureFlags();
  const { ip } = useLocationInfo();
  const { isOpenPopupVoiceCloningOptions } = useSelector(
    (state) => state.voiceVoiceCloning,
  );

  const user = useSelector((state) => state.auth.user);
  const apiTtsSttLaunchEnabled = getFeatureValue(
    FEATURE_KEYS.LAUNCH_API_TTS_STT,
    {
      userId: user?.id,
      email: user?.email,
      phoneNumber: user?.phoneNumber,
      ip,
    },
  );
  const maxInstantVoiceCloning =
    user?.studioUsageOptions?.maxInstantVoiceCloning;
  const maxProVoiceCloning = user?.studioUsageOptions?.maxCloningVoices;

  const filterVoicesByType = (type) =>
    voices.filter((item) => item.type === type);

  const filterVoicesByStatus = (proVoices, statusList) =>
    proVoices.filter((item) => statusList.includes(item.status));

  const instantVoices = useMemo(
    () => filterVoicesByType(VOICE_CLONE_TYPE.ZERO_SHOT),
    [voices],
  );

  const professionalVoices = useMemo(
    () =>
      voices.filter(
        (item) =>
          !item.type ||
          item.type === VOICE_CLONE_TYPE.FINETUNE_SCRIPTS_RECORDING ||
          item.type === VOICE_CLONE_TYPE.FINETUNE_LONG_AUDIO,
      ),
    [voices],
  );

  const processingVoices = useMemo(
    () => filterVoicesByStatus(professionalVoices, VOICE_PROCESSING),
    [professionalVoices],
  );
  const completedVoices = useMemo(
    () => filterVoicesByStatus(professionalVoices, VOICE_COMPLETED),
    [professionalVoices],
  );
  const notUsedVoices = useMemo(
    () => filterVoicesByStatus(professionalVoices, VOICE_NOT_USED),
    [professionalVoices],
  );

  const handleOpenPopupVoiceCloningOption = () => {
    dispatch(actions.voiceVoiceCloning.openPopupVoiceCloningOptions());
  };

  const handleClosePopupVoiceCloningOption = () => {
    dispatch(actions.voiceVoiceCloning.closePopupVoiceCloningOptions());
  };

  const handleChangeSearch = (e) => {
    setSearch(e.target.value);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const filterVoices = (word) => {
    if (!word) {
      setVoices(voiceList);
      return;
    }

    const keyword = nonAccentVietnamese(word);
    const pattern = new RegExp(keyword, 'i');
    const result = voiceList.filter((voice) => {
      const voiceName = nonAccentVietnamese(voice.name);
      return pattern.test(voiceName);
    });
    setVoices(result);
  };

  const debouncedFilterVoices = debounce((word) => {
    filterVoices(word);
  }, DEBOUNCE_DELAY);

  useEffect(() => {
    debouncedFilterVoices(search);

    return () => {
      debouncedFilterVoices.cancel();
    };
  }, [search]);

  useEffect(() => {
    if (search) {
      filterVoices(search);
      return;
    }
    setVoices(voiceList);
  }, [voiceList, search]);

  useEffect(() => {
    if (voiceCloningType === VOICE_CLONING_TYPE.CREATE) {
      handleOpenPopupVoiceCloningOption();
      history.replace(ROUTES.VOICE_CLONING_VOICES);
    }
  }, [voiceCloningType]);

  const getCountText = (count, maxCount) => {
    if (maxCount) {
      return `${count}/${maxCount}`;
    }
    return '';
  };

  const tabs = [
    {
      label: 'instantVoiceClone',
      count: getCountText(instantVoices?.length, maxInstantVoiceCloning),
      name: INSTANT_VOICE_CLONE,
    },
    {
      label: 'proVoiceClone',
      count: getCountText(professionalVoices?.length, maxProVoiceCloning),
      name: PRO_VOICE_CLONE,
      isNew: !apiTtsSttLaunchEnabled,
    },
  ];

  const DialogContent = () => (
    <StyledVoiceCloningOption>
      <InstantVoiceCloningOption
        numOfInstantVoiceCloning={instantVoices?.length}
        handleOpenPopupInstantVoiceCloning={() => {
          handleOpenPopupInstantVoiceCloning();
          handleClosePopupVoiceCloningOption();
        }}
      />
      <ProVoiceCloningOption
        numOfProVoiceCloning={professionalVoices?.length}
        handleOpenPopupProfessionalVoiceCloneUpload={
          handleOpenPopupProfessionalVoiceCloneUpload
        }
      />
    </StyledVoiceCloningOption>
  );

  const HeaderVoiceList = () => (
    <StyledHeaderVoiceList>
      <Typography variant="body-20-bold" color={COLOR.darkGray}>
        {t('voiceList')}
      </Typography>
      <CommonButton
        variant="outlineSmall"
        customStyle={{
          borderRadius: '8px',
        }}
        textProps={{
          variantText: 'body-14-medium',
          color: COLOR.darkBlue,
        }}
        startIcon={
          <MonetizationOnRoundedIcon
            sx={{ fontSize: '24px', color: COLOR.navyBlue }}
          />
        }
        onClick={async () => {
          const hasAccess = ttsUser?.hasEverHadCommunityVoice;
          if (hasAccess) {
            history.push(ROUTES.EARNINGS_MANAGEMENT);
            return;
          }
          try {
            const response = await checkHasEverHadCommunityVoice();
            if (response?.result?.hasAccess && response?.status) {
              dispatch(actions.user.getTtsUser());
              // Wait for call api getTtsUser and update state
              setTimeout(() => {
                history.push(ROUTES.EARNINGS_MANAGEMENT);
              }, 1200);
              return;
            }
            setShowPopupNotEligibleForMonetization(true);
          } catch (error) {
            dispatch(
              actions.noti.push({
                severity: 'error',
                message: 'errorOccurred',
              }),
            );
          }
        }}
      >
        {t('metricAndEarn')}
      </CommonButton>
    </StyledHeaderVoiceList>
  );

  return (
    <StyledVoiceCloningList>
      <Card padding="40px" className="voice-cloning-list-card">
        <Box className="voice-cloning-wrapper">
          {showPromoBanner && (
            <PromoBannerVoiceCloning
              closePromoBannerVoiceCloning={() => setShowPromoBanner(false)}
            />
          )}
          <HeaderVoiceList />
          <Box className="voice-search-bar">
            <SearchInput
              value={search}
              name="search"
              placeholder="searchByVoiceName"
              iconStart={<Search sx={{ fontSize: '16px' }} />}
              onChange={handleChangeSearch}
              styleInput={{ paddingLeft: '12px' }}
              className="voice-search-input"
            />
            <CommonButton
              onClick={handleOpenPopupVoiceCloningOption}
              variant="containedSmall"
              color="primary"
              customStyle={{
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <AddRounded />
              {t('createVoice')}
            </CommonButton>
          </Box>
          <Tabs
            tabs={tabs}
            activeTab={activeTab}
            onChange={handleTabChange}
            styleTab={{
              padding: '10px 12px',
            }}
          />
          <Voices
            activeTab={activeTab}
            handleTabChange={handleTabChange}
            instantVoices={instantVoices || []}
            processingVoices={processingVoices}
            completedVoices={completedVoices}
            notUsedVoices={notUsedVoices}
            totalProVoices={professionalVoices?.length}
            voices={voiceList}
            setVoices={setVoiceList}
            isSearch={Boolean(search)}
            handleOpenPopupInstantVoiceCloning={
              handleOpenPopupInstantVoiceCloning
            }
            onContinueUploadVoice={onContinueUploadVoice}
          />
        </Box>
      </Card>
      <CommonDialog
        width="640px"
        fullWidth
        open={isOpenPopupVoiceCloningOptions}
        onClose={handleClosePopupVoiceCloningOption}
        title={t('voiceCloningOption')}
        contentComponent={<DialogContent />}
      />
      {showPopupNotEligibleForMonetization && (
        <ActionDialog
          open={showPopupNotEligibleForMonetization}
          title={t('notEligibleForMonetization')}
          styleTitle={{
            textAlign: 'center',
          }}
          styleDescription={{
            textAlign: 'center',
            color: COLOR.darkGray,
          }}
          onClose={() => setShowPopupNotEligibleForMonetization(false)}
          description={t('notEligibleForMonetizationDescription')}
          image={WarningIcon}
          imageContainerSize="80px"
          width="512px"
          actionComponent={
            <CommonButton
              variant="contained"
              onClick={() => {
                const url =
                  'https://help.vbee.vn/huong-dan-su-dung/nhan-ban-giong-voice-cloning/chia-se-va-kiem-tien-voi-giong-cong-dong/chia-se-giong-cong-dong';
                window.open(url, '_blank');
              }}
              textProps={{
                variantText: 'body-14-bold',
                color: COLOR.indigo[100],
              }}
            >
              {t('viewTheGuide')}
            </CommonButton>
          }
        />
      )}
    </StyledVoiceCloningList>
  );
};

export default VoiceCloningList;
